

import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { getUsers as apiGetUsers, updateUserProfile as apiUpdateUserProfile, createUser as apiCreateUser } from '@/features/users/api';
import { Profile, UserRole } from '@/features/users/types';
import { useSupabaseAuth } from '@/shared/hooks/useSupabaseAuth';

/**
 * キャッシュキーを生成する
 * @returns キャッシュキー
 */
const getCacheKey = () => '/users';

/**
 * ユーザー管理フック（SWRベース）
 * @returns ユーザー管理フック
 */
export const useUsers = () => {
  const { isAdmin, isLoading: isAuthLoading } = useSupabaseAuth();
  const cacheKey = getCacheKey();

  // SWRでユーザーデータを取得
  const { data: users, error: swrError, isLoading: isUsersLoading, mutate: swrMutate } = useSWR<Profile[]>(
    // 認証状態が確定してからフェッチを開始
    isAuthLoading ? null : cacheKey,
    async () => {
      try {
        const { items } = await apiGetUsers();
        return items;
      } catch (err) {
        console.error('Error fetching users:', err);
        throw new Error('ユーザー情報の取得に失敗しました。');
      }
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 300000, // 5分間
      revalidateOnReconnect: true,
    }
  );

  // 統合されたローディング状態
  const isLoading = useMemo(() => {
    return isAuthLoading || isUsersLoading;
  }, [isAuthLoading, isUsersLoading]);

  // エラー状態
  const error = useMemo(() => {
    return swrError?.message || null;
  }, [swrError]);

  /**
   * ユーザー情報を手動で再取得
   */
  const fetchUsers = useCallback(async () => {
    await swrMutate();
  }, [swrMutate]);

  /**
   * ユーザー情報を更新
   * @param userId ユーザーID
   * @param updates 更新内容
   * @returns 更新後のユーザー情報
   */
  const updateUser = useCallback(
    async (userId: string, updates: Partial<Pick<Profile, 'username' | 'name' | 'role' | 'is_active' | 'craftsman_id'>>) => {
      // フロントエンドでのAdmin権限チェック
      if (!isAdmin) {
        throw new Error('Unauthorized: Admin role required');
      }

      try {
        const updatedUser = await apiUpdateUserProfile(userId, updates);

        // SWRキャッシュを更新（楽観的更新）
        await swrMutate(
          (currentUsers) => {
            if (!currentUsers) return currentUsers;
            return currentUsers.map((user) =>
              user.id === userId ? { ...user, ...updatedUser } : user
            );
          },
          { revalidate: false }
        );

        return updatedUser;
      } catch (err) {
        console.error('Error updating user:', err);
        const message = err instanceof Error ? err.message : 'ユーザー情報の更新に失敗しました。';
        throw new Error(message);
      }
    },
    [isAdmin, swrMutate]
  );

  /**
   * ユーザーを追加
   * @param userData ユーザー情報
   * @returns 追加後のユーザー情報
   */
  const addUser = useCallback(
    async (userData: {
      username: string;
      name: string;
      email: string;
      password: string;
      role: UserRole;
      is_active: boolean;
      craftsman_id: string | null;
    }) => {
      // フロントエンドでのAdmin権限チェック
      if (!isAdmin) {
        throw new Error('Unauthorized: Admin role required');
      }

      try {
        const newUser = await apiCreateUser(userData);

        // SWRキャッシュを更新（楽観的更新）
        await swrMutate(
          (currentUsers) => {
            if (!currentUsers) return [newUser];
            return [...currentUsers, newUser];
          },
          { revalidate: false }
        );

        return newUser;
      } catch (err) {
        console.error('Error creating user:', err);
        const message = err instanceof Error ? err.message : 'ユーザーの作成に失敗しました。';
        throw new Error(message);
      }
    },
    [isAdmin, swrMutate]
  );

  return {
    users: users || [],
    isLoading,
    error,
    fetchUsers,
    updateUser,
    addUser,
    mutate: swrMutate, // SWRのmutate関数を公開
    cacheKey, // キャッシュキーを公開（デバッグ用）
  };
}; 